import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, FileText, Shield, CheckCircle, Sparkles, Loader2, Paperclip } from 'lucide-react';
import Header from './Header';
import { formatDate } from '../utils/dateFormatter';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Label } from './ui/label';
import BulkAttachPdfDialog from './BulkAttachPdfDialog';
import PolicyDeduplicateDialog from './PolicyDeduplicateDialog';
import { Textarea } from './ui/textarea';
import PolicyDetail from './PolicyDetail';

// Using relative URLs

const PolicyList = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  // Top-level tab: 'active' (draft + published) | 'archived'. Replaces the
  // previous habit of using statusFilter='archived' to switch contexts.
  const [policyTab, setPolicyTab] = useState('active');
  const [stats, setStats] = useState(null);
  // Standalone (non-framework) policy creation
  const [standalonePolicyOpen, setStandalonePolicyOpen] = useState(false);
  const [standalonePolicyName, setStandalonePolicyName] = useState('');
  const [standalonePolicyDesc, setStandalonePolicyDesc] = useState('');
  const [standalonePolicyLoading, setStandalonePolicyLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('archive');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkAttachOpen, setBulkAttachOpen] = useState(false);
  const [modalPolicyId, setModalPolicyId] = useState(null);
  // Deduplicate feature — button on the "Select all …" row opens a
  // review dialog with per-group merge preview + explicit confirmation.
  const [dedupOpen, setDedupOpen] = useState(false);
  // Trust Center inclusion map — used to render a subtle "TC" badge on
  // each row. Fetched in one batch call so the list stays fast even
  // with hundreds of policies. Silently disabled if the endpoint fails.
  const [tcInclusionMap, setTcInclusionMap] = useState({});
  // Ref to the PolicyDetail modal child — lets us intercept close attempts
  // (X icon, Esc, overlay click) so we can show the unsaved-changes prompt
  // before actually closing.
  const policyDetailRef = useRef(null);

  useEffect(() => {
    fetchPolicies();
    fetchStats();
  }, []);

  useEffect(() => {
    filterPolicies();
  }, [policies, searchQuery, statusFilter, frameworkFilter, policyTab]);

  const fetchPolicies = async () => {
    try {
      // Include archived rows so the "Archived" tab (client-side filtered
      // by policyTab) can actually show them. Dedupe merges the duplicate
      // policies as archived — without this flag they would silently
      // disappear from every tab.
      const response = await axios.get(`/api/policies`, { params: { include_archived: true } });
      const list = response.data || [];
      setPolicies(list);
      // Fire-and-forget batch inclusion lookup — non-blocking, best
      // effort. Failure just leaves the "TC" badge hidden.
      try {
        const ids = list.map((p) => p.id).filter(Boolean);
        if (ids.length) {
          const { data } = await axios.post(
            '/api/trust-center/inclusion/policies/batch',
            { policy_ids: ids },
          );
          setTcInclusionMap((data && data.map) || {});
        }
      } catch (_e) { /* silent — badge is optional */ }
    } catch (error) {
      // Don't show error toast - just set empty array
      // The UI will handle the empty state gracefully
      setPolicies([]);
      console.log('No policies or error loading policies:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`/api/policies/compliance/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const getPolicyName = (policy) => {
    // Fallback logic: try name first, then title, then 'Unknown'
    return policy.name || policy.title || 'Unknown';
  };

  const filterPolicies = () => {
    // Tab gate first: 'archived' shows only archived policies, 'active' shows
    // everything else (draft + published). The status dropdown below the tab
    // then refines within that scope.
    let filtered = policies.filter(p =>
      policyTab === 'archived' ? p.status === 'archived' : p.status !== 'archived'
    );

    if (searchQuery) {
      filtered = filtered.filter(policy => {
        const policyName = getPolicyName(policy);
        return policyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (policy.description && policy.description.toLowerCase().includes(searchQuery.toLowerCase()));
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(policy => policy.status === statusFilter);
    }

    if (frameworkFilter !== 'all') {
      filtered = filtered.filter(policy => 
        policy.frameworks && 
        Array.isArray(policy.frameworks) && 
        policy.frameworks.includes(frameworkFilter)
      );
    }

    setFilteredPolicies(filtered);
  };

  const calculateFrameworkStats = () => {
    const frameworkCount = {};
    policies.forEach(policy => {
      if (policy.status === 'archived') return; // exclude archived
      if (policy.frameworks && Array.isArray(policy.frameworks)) {
        policy.frameworks.forEach(framework => {
          frameworkCount[framework] = (frameworkCount[framework] || 0) + 1;
        });
      }
    });
    return frameworkCount;
  };

  const calculateStatusStats = () => {
    const statusCount = {
      draft: 0,
      published: 0,
      archived: 0
    };
    policies.forEach(policy => {
      if (statusCount[policy.status] !== undefined) {
        statusCount[policy.status]++;
      }
    });
    return statusCount;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Reset selection when filters change
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, frameworkFilter, searchQuery, policyTab]);

  // Reset the inner status dropdown when switching tabs — its options
  // depend on the active tab (Archived tab has no draft/published split).
  useEffect(() => {
    setStatusFilter('all');
  }, [policyTab]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Determine bulk action from the active tab.
  const viewingArchived = policyTab === 'archived';
  // Only include items whose status matches the action we'd perform
  const selectableIds = filteredPolicies
    .filter(p => viewingArchived ? p.status === 'archived' : p.status !== 'archived')
    .map(p => p.id);

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const openBulkDialog = (action) => {
    setBulkAction(action);
    setBulkDialogOpen(true);
  };

  const handleBulkConfirm = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const endpoint = bulkAction === 'archive' ? 'bulk-archive' : 'bulk-unarchive';
      const response = await axios.post(`/api/policies/${endpoint}`, { ids: selectedIds });
      const count = bulkAction === 'archive'
        ? response.data.archived_count
        : response.data.unarchived_count;
      // Refresh policies list to reflect status changes
      await fetchPolicies();
      toast.success(`${count} polic${count === 1 ? 'y' : 'ies'} ${bulkAction === 'archive' ? 'archived' : 'unarchived'}`);
      setSelectedIds([]);
      setBulkDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${bulkAction} policies`);
    } finally {
      setBulkProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Policy Management</h1>
          <p className="text-slate-600">Create and manage compliance policies for your organization</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Policies</CardDescription>
              <CardTitle className="text-3xl">{policies.filter(p => p.status !== 'archived').length}</CardTitle>
              <div className="text-xs text-slate-500 mt-2">
                {(() => {
                  const statusStats = calculateStatusStats();
                  return `${statusStats.draft} draft, ${statusStats.published} published, ${statusStats.archived} archived`;
                })()}
              </div>
            </CardHeader>
          </Card>
          
          {stats && (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Published Policies</CardDescription>
                  <CardTitle className="text-3xl text-navy-600">{stats.total_policies}</CardTitle>
                  <div className="text-xs text-slate-500 mt-2">Active for compliance</div>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Employees</CardDescription>
                  <CardTitle className="text-3xl">{stats.total_employees}</CardTitle>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Acceptances</CardDescription>
                  <CardTitle className="text-3xl">{stats.total_acceptances}/{stats.total_required_acceptances}</CardTitle>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Compliance Rate</CardDescription>
                  <CardTitle className="text-3xl text-navy-600">{stats.compliance_percentage}%</CardTitle>
                </CardHeader>
              </Card>
            </>
          )}
        </div>

        {/* Framework Breakdown */}
        {policies.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Policies by Framework</CardTitle>
                  <CardDescription>
                    Click on a framework to filter policies. Policies can belong to multiple frameworks.
                    {frameworkFilter !== 'all' && (
                      <span className="ml-2 text-navy-600 font-medium">
                        (Filtered by: {frameworkFilter})
                      </span>
                    )}
                  </CardDescription>
                </div>
                {frameworkFilter !== 'all' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFrameworkFilter('all')}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* All Frameworks Card */}
                <button
                  onClick={() => setFrameworkFilter('all')}
                  className={`rounded-lg p-4 border-2 transition-all duration-200 text-left ${
                    frameworkFilter === 'all'
                      ? 'bg-emerald-50 border-navy-500 shadow-md'
                      : 'bg-slate-50 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Shield className={`w-5 h-5 ${frameworkFilter === 'all' ? 'text-navy-600' : 'text-slate-400'}`} />
                    <span className={`text-2xl font-bold ${frameworkFilter === 'all' ? 'text-navy-600' : 'text-slate-900'}`}>
                      {policies.filter(p => p.status !== 'archived').length}
                    </span>
                  </div>
                  <p className={`text-sm font-medium ${frameworkFilter === 'all' ? 'text-navy-700' : 'text-slate-700'}`}>
                    All Frameworks
                  </p>
                </button>

                {/* Individual Framework Cards */}
                {(() => {
                  const frameworkStats = calculateFrameworkStats();
                  return Object.keys(frameworkStats).length > 0 ? (
                    Object.entries(frameworkStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([framework, count]) => (
                        <button
                          key={framework}
                          onClick={() => setFrameworkFilter(framework)}
                          className={`rounded-lg p-4 border-2 transition-all duration-200 text-left ${
                            frameworkFilter === framework
                              ? 'bg-emerald-50 border-navy-500 shadow-md'
                              : 'bg-slate-50 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Shield className={`w-5 h-5 ${frameworkFilter === framework ? 'text-navy-600' : 'text-slate-400'}`} />
                            <span className={`text-2xl font-bold ${frameworkFilter === framework ? 'text-navy-600' : 'text-slate-900'}`}>
                              {count}
                            </span>
                          </div>
                          <p className={`text-sm font-medium ${frameworkFilter === framework ? 'text-navy-700' : 'text-slate-700'}`}>
                            {framework}
                          </p>
                        </button>
                      ))
                  ) : null;
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active / Archived tabs */}
        {(() => {
          const counts = policies.reduce(
            (acc, p) => {
              if (p.status === 'archived') acc.archived += 1;
              else acc.active += 1;
              return acc;
            },
            { active: 0, archived: 0 }
          );
          const tabBtn = (id, label, count) => {
            const active = policyTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPolicyTab(id)}
                data-testid={`policy-tab-${id}`}
                aria-current={active ? 'page' : undefined}
                className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 ${
                  active
                    ? 'text-navy-700 border-navy-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  {label}
                  <span className={`inline-flex items-center justify-center rounded-full text-[11px] tabular-nums px-1.5 min-w-[20px] h-5 ${
                    active ? 'bg-navy-50 text-navy-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {count}
                  </span>
                </span>
              </button>
            );
          };
          return (
            <div className="mb-4 border-b border-slate-200 flex items-center gap-1" data-testid="policy-tabs">
              {tabBtn('active', 'Active', counts.active)}
              {tabBtn('archived', 'Archived', counts.archived)}
            </div>
          );
        })()}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
              <div className="flex-1 w-full lg:w-auto flex gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search policies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter — options scope to the active tab.
                    On the Archived tab the dropdown collapses to a single
                    'Archived' option since the tab itself already filters. */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                  data-testid="policy-status-filter"
                  disabled={policyTab === 'archived'}
                >
                  {policyTab === 'archived' ? (
                    <option value="all">Archived</option>
                  ) : (
                    <>
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </>
                  )}
                </select>
              </div>

              {/* Create Policy Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setStandalonePolicyOpen(true)}
                  variant="outline"
                  className="border-navy-200 text-navy-700 hover:bg-navy-50"
                  data-testid="create-standalone-policy-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Custom Policy
                </Button>
                <Button
                  onClick={() => navigate('/policies/wizard')}
                  className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                  data-testid="create-policies-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Policies
                </Button>
              </div>
            </div>

            {/* Bulk Selection Bar */}
            {selectableIds.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3" data-testid="policy-bulk-action-bar">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    data-testid="policy-select-all-checkbox"
                  />
                  <span className="text-sm text-slate-700">
                    {selectedIds.length > 0
                      ? `${selectedIds.length} selected`
                      : `Select all ${viewingArchived ? 'archived' : 'non-archived'} (${selectableIds.length})`}
                  </span>
                </label>
                {/* Deduplicate — opens a review screen listing groups of
                    duplicate policies. Sits on the SAME row as the
                    Select-all label (aligned right), styled to match the
                    outline Button primitive used by Archive/Attach so the
                    row reads as one consistent toolbar. Only shown for
                    non-archived view — dedup runs on live policies. */}
                {!viewingArchived && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDedupOpen(true)}
                    className="ml-auto text-blue-700 border-blue-300 hover:bg-blue-50"
                    data-testid="policy-deduplicate-btn"
                    aria-label="Review and merge duplicate policies"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Deduplicate
                  </Button>
                )}
                {selectedIds.length > 0 && (
                  <>
                    {viewingArchived ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBulkDialog('unarchive')}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        data-testid="policy-bulk-unarchive-btn"
                      >
                        Unarchive Selected
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBulkDialog('archive')}
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        data-testid="policy-bulk-archive-btn"
                      >
                        Archive Selected
                      </Button>
                    )}
                    {/* Bulk PDF attachment — sits immediately to the RIGHT
                        of "Archive Selected" per spec. Disabled until at
                        least one policy is selected (implicitly true here
                        because the whole action bar renders only when
                        selectedIds.length > 0). Attaches a single uploaded
                        PDF to every selected policy without altering
                        content or versions. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkAttachOpen(true)}
                      className="text-blue-700 border-blue-300 hover:bg-blue-50"
                      data-testid="policy-bulk-attach-btn"
                      disabled={selectedIds.length === 0}
                      aria-label="Attach PDF to selected policies"
                    >
                      <Paperclip className="w-4 h-4 mr-1.5" /> Attach PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedIds([])}
                      className="text-slate-500"
                      data-testid="policy-bulk-clear-btn"
                    >
                      Clear
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Policies Table — compact, no pagination */}
        {filteredPolicies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No policies found</h3>
              <p className="text-slate-600 mb-6">
                {policies.length === 0 
                  ? 'Get started by creating your first policy'
                  : 'Try adjusting your search or filters'}
              </p>
              {policies.length === 0 && (
                <Button
                  onClick={() => navigate('/policies/wizard')}
                  className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Policy
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table data-testid="policies-table">
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="w-10 pl-4">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        data-testid="policy-table-select-all"
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="w-12 text-xs uppercase tracking-wide text-slate-500 text-right pr-2">#</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500">Policy</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 w-24">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500">Frameworks</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 w-40">Acceptance</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 w-20">Version</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 w-32 pr-4">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPolicies.map((policy, idx) => {
                    const selected = selectedIds.includes(policy.id);
                    const acceptance = policy.acceptance_stats;
                    return (
                      <TableRow
                        key={policy.id}
                        className={`cursor-pointer hover:bg-slate-50 ${selected ? 'bg-emerald-50/40' : ''}`}
                        onClick={() => setModalPolicyId(policy.id)}
                        data-testid={`policy-row-${policy.id}`}
                      >
                        <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => toggleSelect(policy.id)}
                            data-testid={`policy-select-${policy.id}`}
                            aria-label={`Select ${getPolicyName(policy)}`}
                          />
                        </TableCell>
                        <TableCell className="pr-2 text-right text-xs text-slate-400 tabular-nums" data-testid={`policy-row-index-${policy.id}`}>
                          {idx + 1}
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <FileText className="w-4 h-4 text-navy-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate" title={getPolicyName(policy)}>
                                {getPolicyName(policy)}
                              </p>
                              {policy.description && (
                                <p className="text-xs text-slate-500 truncate max-w-[420px]" title={policy.description}>
                                  {policy.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className={`${getStatusBadgeColor(policy.status)} text-[10px] uppercase tracking-wide`}>
                              {policy.status}
                            </Badge>
                            {tcInclusionMap[policy.id] && (
                              <span
                                title="Included in Trust Center"
                                data-testid={`policy-tc-chip-${policy.id}`}
                                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                              >
                                TC
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {policy.frameworks && policy.frameworks.length > 0 ? (
                              policy.frameworks.slice(0, 3).map((fw) => (
                                <Badge key={fw} variant="outline" className="text-[10px] px-1.5 py-0">
                                  {fw}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                            {policy.frameworks && policy.frameworks.length > 3 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">
                                +{policy.frameworks.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {acceptance && acceptance.total_employees > 0 ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-navy-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-700 leading-tight">
                                  {acceptance.accepted_count}/{acceptance.total_employees}
                                </p>
                                <div className="w-20 bg-slate-200 rounded-full h-1 mt-0.5">
                                  <div
                                    className="bg-navy-600 h-1 rounded-full"
                                    style={{ width: `${Math.min(100, acceptance.acceptance_rate || 0)}%` }}
                                  />
                                </div>
                              </div>
                              <span className="text-[11px] text-slate-500 tabular-nums">
                                {Math.round(acceptance.acceptance_rate || 0)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-slate-600 tabular-nums">v{policy.version}</span>
                        </TableCell>
                        <TableCell className="pr-4">
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {formatDate(policy.created_at)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                <span>Showing {filteredPolicies.length} of {policies.length} policies</span>
                {selectedIds.length > 0 && (
                  <span className="text-slate-700 font-medium">{selectedIds.length} selected</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Archive/Unarchive Dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <AlertDialogContent data-testid="policy-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkAction === 'archive' ? 'Archive Selected Policies?' : 'Unarchive Selected Policies?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === 'archive'
                ? `You are about to archive ${selectedIds.length} polic${selectedIds.length === 1 ? 'y' : 'ies'}. Archived policies can be restored later.`
                : `You are about to unarchive ${selectedIds.length} polic${selectedIds.length === 1 ? 'y' : 'ies'}. They will be reset to Draft status.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="policy-bulk-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkConfirm}
              disabled={bulkProcessing}
              data-testid="policy-bulk-dialog-confirm"
            >
              {bulkProcessing
                ? 'Processing...'
                : bulkAction === 'archive' ? 'Archive' : 'Unarchive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Policy Detail Modal */}
      <Dialog
        open={Boolean(modalPolicyId)}
        onOpenChange={(open) => {
          if (open) return;
          // Intercept all close attempts (X, Esc, overlay click). If the
          // child editor is dirty, requestExit pops the save/discard
          // prompt and returns false; otherwise it runs the close
          // continuation immediately.
          const doClose = () => {
            setModalPolicyId(null);
            fetchPolicies();
            fetchStats();
          };
          const detail = policyDetailRef.current;
          if (detail && typeof detail.requestExit === 'function') {
            detail.requestExit(doClose);
          } else {
            doClose();
          }
        }}
      >
        <DialogContent
          // Root modal is now the single vertical scroll container. Removed
          // the inner `flex-1 overflow-y-auto` wrapper — that nested scroll
          // container was capturing wheel events in a way that only the
          // scrollbar-thumb responded to (radix + tall child grid caused
          // the wheel target to land on siblings that had no overflow).
          // `overscroll-behavior: contain` keeps trackpad flicks from
          // bleeding to the page underneath. See scroll bug repro on
          // /policies with the detail panel open.
          className="max-w-[96vw] w-[96vw] h-[95vh] p-0 overflow-y-auto overscroll-contain translate-x-[-50%] translate-y-[-50%] sm:rounded-lg flex flex-col [&>button.absolute]:hidden"
          data-testid="policy-detail-modal"
        >
          <DialogTitle className="sr-only">Policy detail</DialogTitle>
          {modalPolicyId && (
            <PolicyDetail
              ref={policyDetailRef}
              policyId={modalPolicyId}
              isModal
              onClose={() => {
                setModalPolicyId(null);
                fetchPolicies();
                fetchStats();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Standalone (framework-less) Policy Creator */}
      <Dialog open={standalonePolicyOpen} onOpenChange={(o) => !o && !standalonePolicyLoading && setStandalonePolicyOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="standalone-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Create a custom policy
            </DialogTitle>
            <DialogDescription>
              Generate a single policy on any topic — not linked to any framework. AI drafts the initial content; you can edit it after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="std-policy-name" className="text-sm font-medium">Policy title / topic <span className="text-red-500">*</span></Label>
              <Input
                id="std-policy-name"
                value={standalonePolicyName}
                onChange={(e) => setStandalonePolicyName(e.target.value)}
                placeholder="e.g., Third-Party Risk Management Policy"
                className="mt-1.5"
                data-testid="standalone-policy-name-input"
                disabled={standalonePolicyLoading}
              />
            </div>
            <div>
              <Label htmlFor="std-policy-desc" className="text-sm font-medium">What should this policy cover? (optional)</Label>
              <Textarea
                id="std-policy-desc"
                value={standalonePolicyDesc}
                onChange={(e) => setStandalonePolicyDesc(e.target.value)}
                placeholder="e.g., Vendor onboarding requirements, security review process, ongoing monitoring cadence..."
                className="mt-1.5 min-h-[100px]"
                data-testid="standalone-policy-desc-input"
                disabled={standalonePolicyLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStandalonePolicyOpen(false)} disabled={standalonePolicyLoading} data-testid="standalone-policy-cancel">Cancel</Button>
            <Button
              onClick={async () => {
                const name = standalonePolicyName.trim();
                if (!name) { toast.error('Please enter a policy topic'); return; }
                // Pull user's company_name (backend needs it for AI drafts).
                let userCompany = '';
                try {
                  const u = JSON.parse(localStorage.getItem('user') || 'null');
                  userCompany = u?.company_name || '';
                } catch { /* no-op */ }
                if (!userCompany.trim()) {
                  toast.error('Set your company name on your Profile first — AI drafting needs it for context.');
                  return;
                }
                setStandalonePolicyLoading(true);
                try {
                  const token = localStorage.getItem('token');
                  const headers = { Authorization: `Bearer ${token}` };
                  // 1) Draft the AI content FIRST so the v1 snapshot ends
                  //    up carrying real policy text (rather than empty →
                  //    "content is being generated" placeholder when the
                  //    user clicks Version 1 in the version dropdown).
                  let content = null;
                  try {
                    const genRes = await axios.post('/api/policies/generate-content', {
                      policy_name: name,
                      description: standalonePolicyDesc.trim(),
                      frameworks: [],
                      company_name: userCompany,
                    }, { headers });
                    content = genRes.data?.content || null;
                  } catch (genErr) {
                    // Non-fatal — we still create the shell so the user
                    // can retry generation from PolicyDetail.
                    console.warn('AI draft generation failed:', genErr);
                    toast.warning('AI draft failed — creating the policy shell so you can retry from the policy page.');
                  }
                  // 2) Create the policy WITH the content already populated.
                  const createRes = await axios.post('/api/policies', {
                    name,
                    description: standalonePolicyDesc.trim(),
                    frameworks: [],
                    content,
                  }, { headers });
                  const policyId = createRes.data?.id;
                  toast.success('Custom policy created');
                  setStandalonePolicyOpen(false);
                  setStandalonePolicyName('');
                  setStandalonePolicyDesc('');
                  fetchPolicies();
                  fetchStats();
                  if (policyId) setModalPolicyId(policyId);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to create policy');
                } finally {
                  setStandalonePolicyLoading(false);
                }
              }}
              disabled={standalonePolicyLoading || !standalonePolicyName.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="standalone-policy-create-btn"
            >
              {standalonePolicyLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Create with AI</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk PDF Attach dialog — opens from the "Attach PDF" button
          in the bulk action bar. Uploads one PDF and links it to every
          currently-selected policy in a single call. Never mutates
          policy content or versions. */}
      <BulkAttachPdfDialog
        open={bulkAttachOpen}
        onOpenChange={setBulkAttachOpen}
        selectedPolicies={policies.filter((p) => selectedIds.includes(p.id))}
        onSuccess={async () => {
          // Retain selection so the user can continue working on the
          // same set (spec explicitly allows either — we keep them).
          await fetchPolicies();
        }}
      />
      {/* Deduplicate — review + merge modal. Refreshes the list on
          successful merge so the union of framework badges appears
          immediately on the surviving canonical row. */}
      <PolicyDeduplicateDialog
        open={dedupOpen}
        onOpenChange={setDedupOpen}
        onCompleted={async () => {
          await fetchPolicies();
          await fetchStats();
        }}
      />
    </div>
  );
};

export default PolicyList;
